import { useCallback, useEffect, useState } from 'react';
import { Loader, Percent, Plus, Trash2 } from 'lucide-react';
import { createRule, deleteRule, listRules, type CommissionRule } from '../api';

/** Reglas de comisión (solo admin).
 *  El nivel 1 es quien vendió; 2 y 3 son el piramidal (quien la refirió).
 *  max_amount es el tope por venta — sin él, el piramidal dispara el costo. */
export function ReglasComision() {
  const [items, setItems] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', value: '', kind: 'percent', level: '1', target_type: '*', target_id: '', max_amount: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setItems(await listRules()); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.value) { setErr('Nombre y valor son obligatorios'); return; }
    setSaving(true); setErr(null);
    try {
      await createRule({
        name: form.name,
        value: form.value,
        kind: form.kind as 'percent' | 'fixed',
        level: Number(form.level),
        target_type: form.target_type,
        target_id: form.target_id || null,
        max_amount: form.max_amount || null,
        active: true,
      } as any);
      setForm({ name: '', value: '', kind: 'percent', level: '1', target_type: '*', target_id: '', max_amount: '' });
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (r: CommissionRule) => {
    if (!confirm(`¿Borrar la regla «${r.name}»?`)) return;
    try { await deleteRule(r.id); await load(); }
    catch (e: any) { setErr(e.message); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Reglas de comisión</h2>
        <p className="text-sm text-muted-foreground">
          Nivel 1 = quien vendió. Niveles 2 y 3 = referidos (piramidal).
          Pon un <b>tope</b> para que el piramidal no dispare el costo.
        </p>
      </div>

      {err && <div className="rounded-lg border border-red-500/25 bg-red-500/10 text-red-400 px-3 py-2 text-sm">{err}</div>}
      {loading && <p className="text-muted-foreground text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <Percent size={24} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Sin reglas todavía.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Sin al menos una regla de nivel 1, <b>no se comisiona nada</b>.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Regla</th>
                <th className="px-3 py-2 font-medium">Aplica a</th>
                <th className="px-3 py-2 font-medium">Nivel</th>
                <th className="px-3 py-2 font-medium text-right">Valor</th>
                <th className="px-3 py-2 font-medium text-right">Tope</th>
                <th className="px-3 py-2 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">
                    {r.target_type === '*' ? 'Toda la org' : `${r.target_type} ${r.target_id ?? ''}`}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                      N{r.level}{r.level > 1 ? ' · referido' : ''}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {r.kind === 'percent' ? `${Number(r.value)}%` : `$${Number(r.value).toLocaleString('es-MX')}`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {r.max_amount ? `$${Number(r.max_amount).toLocaleString('es-MX')}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(r)} className="p-1.5 rounded-md bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/20">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-xl border border-dashed border-border p-4 space-y-3">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Nueva regla</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Nombre (ej. Comisión estándar)"
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none" />
          <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none">
            <option value="percent">Porcentaje</option>
            <option value="fixed">Monto fijo</option>
          </select>
          <input type="number" min={0} value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
            placeholder={form.kind === 'percent' ? '8 = 8%' : '500 = $500'}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none" />
          <select value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none">
            <option value="1">Nivel 1 — quien vendió</option>
            <option value="2">Nivel 2 — referido</option>
            <option value="3">Nivel 3 — referido</option>
          </select>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <select value={form.target_type} onChange={e => setForm(f => ({ ...f, target_type: e.target.value }))}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none">
            <option value="*">Toda la organización</option>
            <option value="trip">Un viaje específico</option>
            <option value="catalog_item">Un ítem de catálogo</option>
            <option value="plan">Un plan</option>
          </select>
          <input value={form.target_id} onChange={e => setForm(f => ({ ...f, target_id: e.target.value }))}
            placeholder="ID (si no es toda la org)" disabled={form.target_type === '*'}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none disabled:opacity-40" />
          <input type="number" min={0} value={form.max_amount} onChange={e => setForm(f => ({ ...f, max_amount: e.target.value }))}
            placeholder="Tope por venta (recomendado)"
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border outline-none" />
          <button onClick={add} disabled={saving}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}Crear regla
          </button>
        </div>
      </div>
    </div>
  );
}
